import { generateBill } from "lib/common/endpoints/endUser";
import { Registrations } from "lib/common/types/registration";
import BookDelivery from "lib/components/BookDelivery";
import { BiDownload, BiPrinter } from "react-icons/bi";
import { Warning, CheckCircle, Clock, User, CreditCard, BookOpen, Hash } from "@phosphor-icons/react";
import ReactCanvasConfetti from "react-canvas-confetti";
import {
  AnimationEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import React from "react";
import { useAppSelector } from "redux/hooks";
import { getUser } from "redux/reducers/userSlice";
import { getAccessToken } from "lib/accessToken";
import { imgUrl, resolveImgUrl } from "utils/constants";

/* ─── Discord join card ─────────────────────────────────────────────────── */
function DiscordJoinCard({ url, label }: { url: string; label: string }) {
  const storageKey = `discord_joined_${url}`;
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    setJoined(localStorage.getItem(storageKey) === "true");
  }, [storageKey]);

  const handleJoin = () => {
    window.open(url, "_blank", "noopener noreferrer");
    localStorage.setItem(storageKey, "true");
    setJoined(true);
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800/40 p-4 mb-3 last:mb-0">
      <div className="flex items-start gap-3 mb-3">
        <Warning className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 m-0">
            {label}
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 m-0">
            Updates, study materials &amp; batch announcements.
          </p>
        </div>
        {joined && (
          <span className="flex items-center gap-1 text-xs font-semibold text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800/40 rounded-full px-2 py-0.5 flex-shrink-0">
            <CheckCircle size={10} />
            Joined
          </span>
        )}
      </div>
      <button
        onClick={handleJoin}
        className="flex items-center gap-2 rounded-lg bg-primary hover:bg-primary-600 active:bg-primary-700 text-white px-4 py-2 text-sm font-semibold transition-colors w-full justify-center"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/img/logo/external/discord.webp"
          alt=""
          className="w-4 h-4"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
        {joined ? "Open Discord again" : "Join Discord"}
      </button>
    </div>
  );
}

/* ─── Print invoice ─────────────────────────────────────────────────────── */
function PrintInvoice({
  registration,
  name,
  feePaid,
  studentName,
  paymentMode,
  logoUrl,
}: {
  registration: Registrations;
  name: string;
  feePaid: string;
  studentName: string;
  paymentMode: string;
  logoUrl: string;
}) {
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-NP", { year: "numeric", month: "long", day: "numeric" });
  const verifiedDate = registration.verifiedAt
    ? new Date(registration.verifiedAt * 1000).toLocaleDateString("en-NP", {
        year: "numeric", month: "long", day: "numeric",
      })
    : dateStr;
  const receiptNo = registration._id.$oid.slice(-8).toUpperCase();

  const tdL: React.CSSProperties = { padding: "9px 12px", fontSize: 13, color: "#374151", textAlign: "left" };
  const tdR: React.CSSProperties = { ...tdL, textAlign: "right", fontWeight: 600 };
  const thS: React.CSSProperties = { padding: "10px 12px", fontSize: 12, fontWeight: 700, textAlign: "left", color: "#fff", backgroundColor: "#1a3dba", letterSpacing: "0.05em", textTransform: "uppercase" };

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#111", maxWidth: 680, margin: "0 auto", padding: "40px 44px", backgroundColor: "#fff" }}>

      {/* ── Header: logo left, title right ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 32 }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "middle", paddingBottom: 0 }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={logoUrl} alt="ClampHook" style={{ height: 40, display: "block" }} />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                Entrance Exam Preparation · clamphook.com
              </div>
            </td>
            <td style={{ verticalAlign: "middle", textAlign: "right" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#1a3dba", letterSpacing: -0.5 }}>RECEIPT</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Receipt #{receiptNo}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Meta: bill-to left, receipt details right ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 28 }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "top", width: "50%", paddingRight: 24 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>Bill To</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 2 }}>{studentName}</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{name}</div>
            </td>
            <td style={{ verticalAlign: "top", textAlign: "right" }}>
              <table style={{ borderCollapse: "collapse", marginLeft: "auto" }}>
                <tbody>
                  {[
                    ["Issue Date", dateStr],
                    ["Verified On", verifiedDate],
                    ["Payment Method", paymentMode],
                    ["Status", "✓ Verified & Confirmed"],
                  ].map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ fontSize: 11, color: "#9ca3af", paddingRight: 16, paddingBottom: 4, textAlign: "right" }}>{label}</td>
                      <td style={{ fontSize: 12, fontWeight: 600, color: "#111", paddingBottom: 4, textAlign: "right", textTransform: "capitalize" }}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Line items table ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 0 }}>
        <thead>
          <tr>
            <th style={{ ...thS, width: 36 }}>#</th>
            <th style={thS}>Description</th>
            <th style={{ ...thS, textAlign: "right" }}>Qty</th>
            <th style={{ ...thS, textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ backgroundColor: "#f8fafc" }}>
            <td style={{ ...tdL, borderBottom: "1px solid #e5e7eb", color: "#9ca3af" }}>1</td>
            <td style={{ ...tdL, borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ fontWeight: 600 }}>{name}</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Course enrollment — single session fee</div>
            </td>
            <td style={{ ...tdR, borderBottom: "1px solid #e5e7eb" }}>1</td>
            <td style={{ ...tdR, borderBottom: "1px solid #e5e7eb" }}>Rs. {feePaid || "—"}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} style={{ padding: "12px", borderTop: "2px solid #e5e7eb" }} />
            <td style={{ padding: "12px 12px", fontSize: 12, color: "#6b7280", fontWeight: 600, borderTop: "2px solid #e5e7eb", textAlign: "right" }}>Total Paid</td>
            <td style={{ padding: "12px 12px", fontSize: 18, fontWeight: 800, color: "#1a3dba", borderTop: "2px solid #e5e7eb", textAlign: "right" }}>
              Rs. {feePaid || "—"}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* ── Footer ── */}
      <div style={{ marginTop: 40, paddingTop: 20, borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 11, color: "#9ca3af" }}>
          Computer-generated receipt. Valid without signature.
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af" }}>
          Thank you for choosing ClampHook!
        </div>
      </div>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────────── */
export default function RegisterDownloadReciept({
  registration,
  name,
  feePaid,
}: {
  registration: Registrations;
  name: string;
  feePaid: string;
}) {
  const refAnimationInstance = useRef(undefined) as React.MutableRefObject<AnimationEventHandler>;

  const getInstance = useCallback((instance) => {
    refAnimationInstance.current = instance;
  }, []);

  const makeShot = useCallback((particleRatio, opts) => {
    refAnimationInstance.current &&
      refAnimationInstance?.current({
        ...opts,
        origin: { y: 0.7 },
        particleCount: Math.floor(200 * particleRatio),
      });
  }, []);

  const fire = useCallback(() => {
    makeShot(0.25, { spread: 26, startVelocity: 55 });
    makeShot(0.2, { spread: 60 });
    makeShot(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    makeShot(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    makeShot(0.1, { spread: 120, startVelocity: 45 });
  }, [makeShot]);

  const user = useAppSelector(getUser);
  const [discordLinks, setDiscordLinks] = useState<{ url: string; label: string }[]>([]);
  const [logoUrl, setLogoUrl] = useState("/img/logo/ClampHook_Logo_Primary.png");

  // Build absolute logo URL for print (relative paths can fail in some browsers)
  useEffect(() => {
    if (typeof window !== "undefined") {
      setLogoUrl(window.location.origin + "/img/logo/ClampHook_Logo_Primary.png");
    }
  }, []);

  // Inject @media print CSS — visibility approach is the most reliable
  useEffect(() => {
    if (!registration?.isVerified) return;
    const id = "clamphook-print-style";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @media print {
        body * { visibility: hidden !important; }
        #print-invoice-root,
        #print-invoice-root * { visibility: visible !important; }
        #print-invoice-root {
          position: fixed !important;
          inset: 0 !important;
          background: #fff !important;
          z-index: 99999 !important;
          overflow: visible !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => { document.getElementById(id)?.remove(); };
  }, [registration?.isVerified]);

  useEffect(() => {
    if (!registration?.isVerified) return;
    const token = getAccessToken();
    if (!token) return;
    fetch("/api/discord/links", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data?.links)) setDiscordLinks(data.links); })
      .catch(() => {});
  }, [registration?.isVerified]);

  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    if (registration?.isVerified) fire();
  }, [registration, fire]);

  if (!registration)
    return (
      <div className="text-center py-8 text-gray-500">
        You need to fill up the registration form first.
      </div>
    );

  const studentName = user?.fullName || "—";
  const paymentMode = registration.paymentDetails?.mode || "—";
  const avatarSrc = user?.userProfilePicture
    ? resolveImgUrl(user.userProfilePicture)
    : user?.userAvatarPicture
    ? `/avatar/${user.userAvatarPicture}`
    : null;
  const initials = studentName.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const infoRows = [
    { icon: <User size={14} />, label: "Student", value: studentName },
    { icon: <BookOpen size={14} />, label: "Course", value: name },
    { icon: <CreditCard size={14} />, label: "Payment", value: paymentMode },
    { icon: <Hash size={14} />, label: "Receipt ID", value: registration._id.$oid.slice(-8).toUpperCase() },
  ];

  return (
    <section className="max-w-3xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 lg:items-start">

        {/* ── LEFT: profile receipt card ── */}
        <div className="w-full lg:w-72 flex-shrink-0">
          {/* No overflow-hidden so the avatar can overlap the banner without being clipped */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">

            {/* Solid banner — rounded-t-2xl to clip its own corners */}
            <div className="bg-primary-600 h-28 w-full rounded-t-2xl grid place-items-center relative">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/img/logo/ClampHook_Logo_Primary.png"
                alt="ClampHook"
                className="h-8 object-contain brightness-0 invert opacity-60"
              />
              <div className="absolute top-3 right-3">
                {registration.isVerified ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 text-white text-xs font-semibold border border-white/30">
                    <CheckCircle size={10} /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 text-white text-xs font-semibold border border-white/30">
                    <Clock size={10} /> Under review
                  </span>
                )}
              </div>
            </div>

            {/* Avatar overlapping banner — relative z-10 so it renders above the banner */}
            <div className="flex flex-col items-center -mt-12 pb-4 px-5 relative z-10">
              <div className="w-24 h-24 rounded-full border-4 border-white dark:border-gray-800 bg-primary-100 dark:bg-primary-900/30 overflow-hidden flex items-center justify-center flex-shrink-0">
                {avatarSrc && !avatarError ? (
                  <>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={avatarSrc}
                      alt={studentName}
                      className="w-full h-full object-cover"
                      onError={() => setAvatarError(true)}
                    />
                  </>
                ) : (
                  <span className="text-2xl font-bold text-primary-600 dark:text-primary-400 select-none">
                    {initials}
                  </span>
                )}
              </div>
              <p className="mt-3 text-base font-bold text-gray-900 dark:text-gray-100 text-center leading-tight m-0">
                {studentName}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 text-center">
                @{user?.userData?.username}
              </p>
              <div className="mt-3 px-4 py-1.5 rounded-full bg-primary-50 dark:bg-blue-950/30 border border-primary-50 dark:border-blue-900/40">
                <span className="text-sm font-bold text-primary-600 dark:text-primary-300">
                  Rs. {feePaid || "—"} paid
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-gray-100 dark:bg-gray-700 mx-5" />

            {/* Info rows */}
            <div className="px-5 py-3">
              {infoRows.map(({ icon, label, value }) => (
                <div key={label} className="flex items-center justify-between py-2.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0 gap-2">
                  <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500 flex-shrink-0">
                    {icon}
                    <span className="text-xs whitespace-nowrap">{label}</span>
                  </div>
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 capitalize flex-1 min-w-0 text-right truncate">
                    {value}
                  </span>
                </div>
              ))}
            </div>

            {/* Pending notice */}
            {!registration.isVerified && (
              <div className="mx-5 mb-5 rounded-xl border border-amber-100 dark:border-amber-900/30 bg-amber-50 dark:bg-amber-950/20 px-4 py-4 text-center">
                <Clock className="text-amber-400 mx-auto mb-2" size={22} />
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 m-0">
                  Payment under review
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-500 mt-1 m-0">
                  We&apos;ll verify within 24 hours. You&apos;ll be notified once confirmed.
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-2 m-0 font-medium">
                  After approval, you&apos;ll need to join the Discord server for your batch to receive study materials &amp; class updates.
                </p>
              </div>
            )}

            {/* Actions */}
            {registration.isVerified && (
              <div className="px-5 pb-5 flex flex-col gap-2">
                <a
                  href={generateBill + registration._id.$oid}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl bg-primary hover:bg-primary-600 active:bg-primary-700 text-white font-semibold text-sm transition-colors no-underline"
                >
                  <BiDownload size={16} />
                  Download Receipt
                </a>
                <button
                  onClick={() => window.print()}
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-sm transition-colors"
                >
                  <BiPrinter size={16} />
                  Print / Save as PDF
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Discord join cards ── */}
        {registration.isVerified && discordLinks.length > 0 && (
          <div className="w-full lg:flex-1">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
              Your Discord Servers
            </p>
            {discordLinks.map((link) => (
              <DiscordJoinCard key={link.url} url={link.url} label={link.label} />
            ))}
          </div>
        )}
      </div>

      {/* Book Delivery — temporarily disabled */}
      {/* <div className="mt-6">
        <BookDelivery registrantId={registration._id.$oid} />
      </div> */}

      {/* Hidden on screen, only visible at print time */}
      {registration.isVerified && (
        <div id="print-invoice-root" style={{ display: "none" }}>
          <PrintInvoice
            registration={registration}
            name={name}
            feePaid={feePaid}
            studentName={studentName}
            paymentMode={paymentMode}
            logoUrl={logoUrl}
          />
        </div>
      )}

      <ReactCanvasConfetti
        refConfetti={getInstance}
        style={{
          position: "fixed",
          pointerEvents: "none",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
        }}
      />
    </section>
  );
}
